import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag,
  Star, Mail, Inbox, Settings as SettingsIcon, Menu, Loader2, RefreshCw,
  Download, Send, RotateCcw, ShieldAlert, ChevronDown, ChevronRight, FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Local copy of the admin sidebar (kept in sync with Admin.tsx)
const adminNav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
];

const Sidebar = ({ open, toggle }: { open: boolean; toggle: () => void }) => {
  const location = useLocation();
  return (
    <>
      <button onClick={toggle} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {adminNav.map((item) => (
            <Link key={item.to} to={item.to}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                location.pathname === item.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"
              }`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
};

type LogRow = {
  id: string;
  message_id: string;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
  metadata: Record<string, any> | null;
};

const RANGES = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
} as const;

const statusBadge = (status: string) => {
  const s = status.toLowerCase();
  if (s === "sent" || s === "delivered")
    return <Badge className="bg-primary/15 text-primary border border-primary/30 font-mono text-[10px] uppercase tracking-wider">Sent</Badge>;
  if (s === "pending" || s === "queued")
    return <Badge className="bg-muted text-muted-foreground border border-border font-mono text-[10px] uppercase tracking-wider">Queued</Badge>;
  if (s === "suppressed")
    return <Badge className="bg-secondary text-secondary-foreground border border-border font-mono text-[10px] uppercase tracking-wider">Suppressed</Badge>;
  if (s === "bounced" || s === "complained")
    return <Badge className="bg-destructive/10 text-destructive border border-destructive/30 font-mono text-[10px] uppercase tracking-wider">{status}</Badge>;
  return <Badge className="bg-destructive/20 text-destructive border border-destructive/40 font-mono text-[10px] uppercase tracking-wider">{status}</Badge>;
};

const AdminEmailLog = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rows, setRows] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<keyof typeof RANGES>("7d");
  const [tplFilter, setTplFilter] = useState<string>("booking-confirmation");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templates, setTemplates] = useState<string[]>([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selftestLoading, setSelftestLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - RANGES[range]).toISOString();
    let q = supabase
      .from("email_send_log")
      .select("id, message_id, template_name, recipient_email, status, error_message, created_at, metadata")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(500);
    if (tplFilter !== "all") q = q.eq("template_name", tplFilter);
    const { data, error } = await q;
    if (error) toast.error(error.message);
    setRows((data ?? []) as LogRow[]);

    // Distinct template names for the filter dropdown
    const { data: tpls } = await supabase
      .from("email_send_log")
      .select("template_name")
      .gte("created_at", new Date(Date.now() - RANGES["30d"]).toISOString())
      .limit(1000);
    const unique = Array.from(new Set((tpls ?? []).map((r: any) => r.template_name as string))).sort();
    setTemplates(unique);
    setLoading(false);
  };

  useEffect(() => { load(); }, [range, tplFilter]);

  // Deduplicate by message_id, keeping latest row per email
  const deduped = useMemo(() => {
    const m = new Map<string, LogRow>();
    for (const r of rows) {
      const key = r.message_id || r.id;
      const prev = m.get(key);
      if (!prev || new Date(r.created_at) > new Date(prev.created_at)) m.set(key, r);
    }
    return Array.from(m.values()).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [rows]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return deduped;
    if (statusFilter === "failed")
      return deduped.filter((r) => ["failed", "dlq", "bounced", "complained"].includes(r.status));
    return deduped.filter((r) => r.status === statusFilter);
  }, [deduped, statusFilter]);

  const retry = async (r: LogRow) => {
    const bookingId = r.metadata?.booking_id as string | undefined;
    if (!bookingId) {
      toast.error("This log row has no booking_id metadata to retry from.");
      return;
    }
    setRetryingId(r.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "resend-booking-confirmation",
        { body: { booking_id: bookingId } }
      );
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`Resent to ${(data as any)?.recipient ?? "recipient"}`);
      load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to resend");
    } finally {
      setRetryingId(null);
    }
  };

  const runSelftest = async () => {
    setSelftestLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "email-unsubscribe-selftest",
        { body: {} }
      );
      if (error) throw error;
      if ((data as any)?.ok) {
        toast.success("Unsubscribe self-test passed — see new suppressed row.");
      } else {
        const failedStep = ((data as any)?.steps ?? []).find((s: any) => !s.ok);
        toast.error(`Self-test failed at step: ${failedStep?.name ?? "unknown"}`);
      }
      load();
    } catch (e: any) {
      toast.error(e?.message || "Self-test failed");
    } finally {
      setSelftestLoading(false);
    }
  };

  const exportCsv = () => {
    const esc = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "created_at", "template_name", "status", "recipient_email",
      "error_message", "correlation_id", "booking_id", "critical_bypass",
    ];
    const lines = [header.join(",")];
    for (const r of filtered) {
      const m = r.metadata || {};
      lines.push([
        r.created_at,
        r.template_name,
        r.status,
        r.recipient_email,
        r.error_message ?? "",
        m.correlation_id ?? "",
        m.booking_id ?? "",
        m.critical ? "true" : "false",
      ].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `email-log-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows`);
  };

  const stats = useMemo(() => {
    const s = { total: deduped.length, sent: 0, pending: 0, failed: 0, suppressed: 0 };
    for (const r of deduped) {
      if (r.status === "sent" || r.status === "delivered") s.sent++;
      else if (r.status === "pending") s.pending++;
      else if (r.status === "suppressed") s.suppressed++;
      else s.failed++;
    }
    return s;
  }, [deduped]);

  return (
    <div className="pt-16 min-h-screen">
      <Sidebar open={sidebarOpen} toggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-display tracking-widest">EMAIL LOG</h1>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={runSelftest} disabled={selftestLoading} className="font-body">
              {selftestLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-1" />}
              Test unsubscribe
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} className="font-body">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={load} className="font-body">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-panel border border-border p-4">
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest mb-1">Total</p>
            <p className="text-2xl font-display text-primary">{stats.total}</p>
          </div>
          <div className="bg-panel border border-border p-4">
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest mb-1">Sent</p>
            <p className="text-2xl font-display text-primary">{stats.sent}</p>
          </div>
          <div className="bg-panel border border-border p-4">
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest mb-1">Suppressed</p>
            <p className="text-2xl font-display text-foreground">{stats.suppressed}</p>
          </div>
          <div className="bg-panel border border-border p-4">
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest mb-1">Failed</p>
            <p className="text-2xl font-display text-destructive">{stats.failed}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Select value={range} onValueChange={(v) => setRange(v as keyof typeof RANGES)}>
            <SelectTrigger className="w-32 font-body"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tplFilter} onValueChange={setTplFilter}>
            <SelectTrigger className="w-56 font-body"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All templates</SelectItem>
              <SelectItem value="booking-confirmation">booking-confirmation</SelectItem>
              {templates.filter((t) => t !== "booking-confirmation").map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 font-body"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="pending">Queued</SelectItem>
              <SelectItem value="suppressed">Suppressed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-panel border border-border p-6">
            <p className="text-muted-foreground font-body text-sm">No email events match.</p>
          </div>
        ) : (
          <div className="bg-panel border border-border overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_2fr_120px_180px] gap-4 px-4 py-3 border-b border-border bg-surface/50">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Template</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recipient / Error</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Time / Actions</p>
            </div>
            {filtered.map((r) => {
              const m = r.metadata || {};
              const isCritical = !!m.critical;
              const bookingId = m.booking_id as string | undefined;
              const correlation = m.correlation_id as string | undefined;
              const isOpen = !!expanded[r.id];
              const canRetry = !!bookingId && r.template_name === "booking-confirmation";
              return (
                <div key={r.id} className="border-b border-border last:border-0">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_120px_220px] gap-2 md:gap-4 px-4 py-3 md:items-start">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-foreground truncate">{r.template_name}</p>
                      {isCritical && (
                        <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono uppercase tracking-wider text-primary">
                          <ShieldAlert className="w-3 h-3" /> Critical bypass
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-body text-sm truncate">{r.recipient_email}</p>
                      {r.error_message && (
                        <p className="text-xs text-destructive font-mono break-words mt-0.5">{r.error_message}</p>
                      )}
                      {correlation && (
                        <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5" title={correlation}>
                          ↳ {correlation}
                        </p>
                      )}
                    </div>
                    <div>{statusBadge(r.status)}</div>
                    <div className="flex flex-col gap-1 md:items-end">
                      <p className="text-xs text-muted-foreground font-mono">{new Date(r.created_at).toLocaleString()}</p>
                      <div className="flex gap-1">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
                          className="h-7 px-2 text-[11px] font-mono"
                        >
                          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          Details
                        </Button>
                        {canRetry && (
                          <Button
                            variant="outline" size="sm"
                            disabled={retryingId === r.id}
                            onClick={() => retry(r)}
                            className="h-7 px-2 text-[11px] font-mono"
                            title="Re-send this booking confirmation"
                          >
                            {retryingId === r.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <RotateCcw className="w-3 h-3" />}
                            Retry
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-4 -mt-1">
                      <div className="bg-surface/40 border border-border p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono">
                        <div><span className="text-muted-foreground">message_id:</span> {r.message_id}</div>
                        <div><span className="text-muted-foreground">correlation_id:</span> {correlation ?? "—"}</div>
                        <div><span className="text-muted-foreground">booking_id:</span> {bookingId ?? "—"}</div>
                        <div>
                          <span className="text-muted-foreground">suppression bypass:</span>{" "}
                          {isCritical
                            ? <span className="text-primary">YES — critical template</span>
                            : <span>no</span>}
                        </div>
                        <div className="sm:col-span-2">
                          <span className="text-muted-foreground">metadata:</span>{" "}
                          <pre className="whitespace-pre-wrap break-all text-[10px] mt-1 text-foreground/80">
                            {JSON.stringify(m, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailLog;